import { useEffect, useState } from 'react';
import { useQueryClient, type Query } from '@tanstack/react-query';

/** True when a query failed because the backend has no demo data stored for its endpoint. */
export function isNoDemoDataError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { error?: string } } } | null)?.response;
  return response?.status === 404 && response?.data?.error === 'no_demo_data';
}

/** True when the query was made for this demo nonce: analytics keys end with `demo:<nonce>`; PBC keys carry `demo_nonce` in their params. */
export function isQueryForDemoNonce(query: Pick<Query, 'queryKey'>, nonce: string): boolean {
  return query.queryKey.some(
    (part) =>
      part === `demo:${nonce}` ||
      (typeof part === 'object' && part !== null && (part as { demo_nonce?: unknown }).demo_nonce === nonce)
  );
}

/**
 * Watches the query cache for queries a demo tile made (identified by its
 * unique nonce) and reports whether any came back "no demo data yet", so the
 * tile can say so instead of showing the widget's generic load error.
 */
export function useNoDemoData(nonce: string | null): boolean {
  const queryClient = useQueryClient();
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setMissing(false);
    if (!nonce) return undefined;
    const cache = queryClient.getQueryCache();
    const check = () =>
      setMissing(
        cache
          .findAll({ predicate: (q) => isQueryForDemoNonce(q, nonce) })
          .some((q) => isNoDemoDataError(q.state.error))
      );
    check();
    return cache.subscribe(check);
  }, [nonce, queryClient]);

  return missing;
}
